import React, { useEffect, useState, useRef } from "react";
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  IconButton,
  Tooltip,
  Autocomplete,
  Checkbox, MenuItem
} from "@mui/material";
import { Navigate } from "react-router-dom";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";

import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useAuth } from "../authContext";
import { logActivity } from "../utils/activityLogger";

import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";


function Entities() {
  const { companyId, user, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (!user) return <Navigate to="/login" />;

  // -------- data ----------
  const [entities, setEntities] = useState([]);

  // -------- filters ----------
  const [nameOptions, setNameOptions] = useState([]); // unique names
  const [typeOptions, setTypeOptions] = useState([]); // unique types
  const [filterNames, setFilterNames] = useState([]);
  const [filterTypes, setFilterTypes] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const NAME_ALL = { id: "__ALL__", label: `Select all (${nameOptions.length})` };
  const TYPE_ALL = { id: "__ALL__", label: `Select all (${typeOptions.length})` };
  const nameOpts = [NAME_ALL, ...nameOptions.map((n) => ({ id: n, label: n }))];
  const typeOpts = [TYPE_ALL, ...typeOptions.map((t) => ({ id: t, label: t }))];

  const allNamesSelected =
    nameOptions.length > 0 && filterNames.length === nameOptions.length;
  const allTypesSelected =
    typeOptions.length > 0 && filterTypes.length === typeOptions.length;




/**
 * Refresh list, options, and keep filter selections in sync.
 * Pass { addedName, addedType } when you create/update so the new record
 * is visible even if a subset of filters is active.
 */
const refreshEntitiesAndFilters = async ({ addedName = null, addedType = null } = {}) => {
  const q = query(collection(db, "entities"), where("companyId", "==", companyId));
  const snap = await getDocs(q);
  const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  setEntities(list);

  // recompute options
  const newNameOpts = Array.from(new Set(list.map((e) => e.name))).sort();
  const newTypeOpts = Array.from(
    new Set(list.map((e) => e.type).filter(Boolean).concat(DEFAULT_TYPES))
  ).sort();

  setNameOptions(newNameOpts);
  setTypeOptions(newTypeOpts);

  // preserve/extend current selections
  const wasAllNamesSelected = allNamesSelected;
  const wasAllTypesSelected = allTypesSelected;

  setFilterNames(
    wasAllNamesSelected
      ? newNameOpts
      : Array.from(
          new Set(
            // keep only values that still exist + include newly added if provided
            filterNames.filter((n) => newNameOpts.includes(n)).concat(addedName ? [addedName] : [])
          )
        )
  );

  setFilterTypes(
    wasAllTypesSelected
      ? newTypeOpts
      : Array.from(
          new Set(
            filterTypes.filter((t) => newTypeOpts.includes(t)).concat(addedType ? [addedType] : [])
          )
        )
  );
};


  // -------- sorting/pagination ----------
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // -------- dialogs ----------
  const [openDialog, setOpenDialog] = useState(false);
  const [currentEntity, setCurrentEntity] = useState({
    id: null,
    name: "",
    type: "",
  });

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const DEFAULT_TYPES = ["Client", "Partner", "Vendor"];

  const mergedTypes = React.useMemo(
    () => [...new Set([...(typeOptions || []), ...DEFAULT_TYPES])].sort(),
    [typeOptions]
  );

  // ------- fetch ----------
  useEffect(() => {
    if (!companyId) return;
      refreshEntitiesAndFilters();
  }, [companyId]);

  // default select all (run once)
  const init = useRef(false);
  useEffect(() => {
    if (!init.current && nameOptions.length) {
      setFilterNames(nameOptions);
    }
    if (!init.current && typeOptions.length) {
      setFilterTypes(typeOptions);
    }
    if (nameOptions.length && typeOptions.length) init.current = true;
  }, [nameOptions, typeOptions]);

  // ------- CRUD ----------
  const handleOpenDialog = (entity = null) => {
    setCurrentEntity(entity ? { ...entity } : { id: null, name: "", type: "" });
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentEntity({ id: null, name: "", type: "" });
  };

  const handleSaveEntity = async () => {
    if (!currentEntity.name || !currentEntity.type) {
      alert("Please fill all fields.");
      return;
    }
    try {
      if (currentEntity.id) {
        const ref = doc(db, "entities", currentEntity.id);
        await updateDoc(ref, {
          name: currentEntity.name,
          type: currentEntity.type,
          companyId,
        });
        await logActivity({
          userId: user?.uid,
          userName: user?.name || "Unknown",
          companyId,
          action: "UPDATE",
          module: "Entity",
          details: `Updated entity "${currentEntity.name}"`,
        });
      } else {
        const ref = await addDoc(collection(db, "entities"), {
          name: currentEntity.name,
          type: currentEntity.type,
          companyId,
          createdOn: new Date().toISOString().split("T")[0],
          createdBy: user?.displayName || user?.email || "Unknown",
        });
        await logActivity({
          userId: user?.uid,
          userName: user?.name || "Unknown",
          companyId,
          action: "CREATE",
          module: "Entity",
          details: `Created entity "${currentEntity.name}"`,
        });
      }
      handleCloseDialog();
       await refreshEntitiesAndFilters({
   addedName: currentEntity.name,
   addedType: currentEntity.type,
 });
    } catch (e) {
      console.error("Error saving entity:", e);
      alert("Failed to save entity.");
    }
  };

  const onRequestDeleteEntity = (entity) => {
    setPendingDelete(entity);
    setConfirmOpen(true);
  };
  const onConfirmDeleteEntity = async () => {
    try {
      if (pendingDelete?.id) {
        await deleteDoc(doc(db, "entities", pendingDelete.id));
        await logActivity({
          userId: user?.uid,
          userName: user?.name || "Unknown",
          companyId,
          action: "DELETE",
          module: "Entity",
          details: `Deleted entity "${pendingDelete.name}"`,
        });
        await refreshEntitiesAndFilters();
      }
    } catch (e) {
      console.error("Delete failed:", e);
      alert("Failed to delete entity.");
    } finally {
      setConfirmOpen(false);
      setPendingDelete(null);
    }
  };

  // ------- filter/sort/paginate ----------
  const filtered = entities.filter((e) => {
    const matchesName = filterNames.length === 0 || filterNames.includes(e.name);
    const matchesType = filterTypes.length === 0 || filterTypes.includes(e.type);
    const matchesStart = !startDate || new Date(e.createdOn) >= new Date(startDate);
    const matchesEnd = !endDate || new Date(e.createdOn) <= new Date(endDate);
    return matchesName && matchesType && matchesStart && matchesEnd;
  });

  const cmp = (a, b) => {
    const va = a ?? "";
    const vb = b ?? "";
    if (sortField === "createdOn") {
      return new Date(va) - new Date(vb);
    }
    return String(va).localeCompare(String(vb), undefined, { sensitivity: "base" });
  };

  const sorted = [...filtered].sort((a, b) => {
    const r = cmp(a[sortField], b[sortField]);
    return sortOrder === "asc" ? r : -r;
  });

  const handleSort = (field) => {
    if (sortField === field) setSortOrder((p) => (p === "asc" ? "desc" : "asc"));
    else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleChangePage = (_, p) => setPage(p);
  const handleChangeRpp = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const paged = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);


const handleClearFilters = () => {
  // clear multi-selects
  setFilterNames(nameOptions);
  setFilterTypes(typeOptions);

  // clear date range
  setStartDate("");
  setEndDate("");

  // reset table state
  setPage(0);
  setSortField("name");
  setSortOrder("asc");
};

// Format "yyyy-mm-dd" => "dd-mm-yyyy" without creating a Date object
const formatDMY = (iso) => {
  if (!iso) return "—";
  const parts = String(iso).split("-");
  if (parts.length !== 3) return iso; // fallback if unexpected
  const [y, m, d] = parts;
  return `${d}-${m}-${y}`;
};


// ---------------- CSV Upload: Entities ----------------
const parseCsvRows = (text) => {
  // Supports quoted cells + commas inside quotes
  const rows = [];
  let row = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = text[i + 1];

    if (ch === '"' && inQuotes && next === '"') {
      cell += '"';
      i++;
      continue;
    }
    if (ch === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (ch === "," && !inQuotes) {
      row.push(cell);
      cell = "";
      continue;
    }
    if ((ch === "\n" || ch === "\r") && !inQuotes) {
      if (ch === "\r" && next === "\n") i++; // CRLF
      row.push(cell);
      cell = "";
      if (row.some((v) => (v ?? "").toString().trim() !== "")) rows.push(row);
      row = [];
      continue;
    }
    cell += ch;
  }
  row.push(cell);
  if (row.some((v) => (v ?? "").toString().trim() !== "")) rows.push(row);
  return rows;
};

const norm = (s) => (s ?? "").toString().trim();
const normLower = (s) => norm(s).toLowerCase();

const normalizeType = (t) => {
  const x = normLower(t);
  if (!x) return "";
  if (x === "client") return "Client";
  if (x === "partner") return "Partner";
  if (x === "vendor") return "Vendor";
  return ""; // invalid
};

const handleUploadEntitiesCsv = async (file) => {
  if (!companyId) {
    alert("Company not found. Please login again.");
    return;
  }
  if (!file) return;

  // Activity log (same pattern you use elsewhere)
  logActivity({
    userId: user?.uid,
    userName: user?.name || "Unknown",
    companyId,
    action: "UPLOAD",
    module: "EntityCSV",
    details: `Selected CSV "${file.name}" (${file.size} bytes).`,
  }).catch(() => {});

  const text = await file.text();
  const grid = parseCsvRows(text);

  if (!grid.length) {
    alert("CSV is empty.");
    return;
  }

  // Header mapping
  const header = grid[0].map((h) => normLower(h));
  const idxName = header.indexOf("name");
  const idxType = header.indexOf("type");

  if (idxName < 0 || idxType < 0) {
    alert('CSV must have headers: "name,type"');
    return;
  }

  // De-dup against existing entities in state (same company)
  const existingKeySet = new Set(
    entities.map((e) => `${normLower(e.name)}|${normLower(e.type)}`)
  );

  let inserted = 0;
  let skipped = 0;
  let failed = 0;
  const errors = [];

  for (let r = 1; r < grid.length; r++) {
    const rowNo = r + 1; // 1-based with header
    const row = grid[r];

    const name = norm(row[idxName]);
    const type = normalizeType(row[idxType]);

    const rowErr = [];
    if (!name) rowErr.push("name is blank");
    if (!type) rowErr.push(`invalid type "${norm(row[idxType])}" (allowed: Client/Partner/Vendor)`);

    if (rowErr.length) {
      failed++;
      errors.push(`Row ${rowNo}: ${rowErr.join("; ")}`);
      continue;
    }

    const key = `${normLower(name)}|${normLower(type)}`;
    if (existingKeySet.has(key)) {
      skipped++;
      continue;
    }

    try {
      await addDoc(collection(db, "entities"), {
        name,
        type,
        companyId,
        createdOn: new Date().toISOString().split("T")[0],
        createdBy: user?.displayName || user?.email || "Unknown",
      });

      existingKeySet.add(key);
      inserted++;
    } catch (e) {
      failed++;
      errors.push(`Row ${rowNo}: ${e?.message || "Failed to add entity"}`);
    }
  }

  // Refresh page lists + filters (your existing function)
  await refreshEntitiesAndFilters();

  // Final activity log
  logActivity({
    userId: user?.uid,
    userName: user?.name || "Unknown",
    companyId,
    action: "UPLOAD",
    module: "EntityCSV",
    details: `Processed CSV "${file.name}". Inserted ${inserted}, Skipped ${skipped}, Failed ${failed}.`,
  }).catch(() => {});

  // Summary alert
  if (failed) {
    alert(
      `Entity CSV processed with warnings.\nInserted: ${inserted}\nSkipped: ${skipped}\nFailed: ${failed}\n\nFirst errors:\n` +
        errors.slice(0, 10).join("\n")
    );
  } else {
    alert(`Entity CSV uploaded successfully.\nInserted: ${inserted}\nSkipped: ${skipped}`);
  }
};


  return (
    <Container maxWidth={false} sx={{ mt: 2 }}>
      <Typography variant="h5" gutterBottom>
        Entities
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
  <Button variant="contained" onClick={() => handleOpenDialog()}>
    Add Entity
  </Button>

  <Button variant="contained" component="label">
    Upload CSV
    
    <input
  type="file"
  accept=".csv"
  hidden
  onChange={async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleUploadEntitiesCsv(file);
    e.target.value = ""; // allow re-upload same file
  }}
/>

  </Button>

  <Button
    variant="outlined"
    onClick={handleClearFilters}
    startIcon={<FilterAltOffIcon />}  // remove this prop if you skipped the icon import
  >
    Clear Filters
  </Button>
</Box>


      {/* Filters — single row, shrink-to-fit */}
      <Paper sx={{ p: 2, borderRadius: 3, mb: 2 }}>
        <Box
          sx={{
            display: "grid",
            gap: 1,
            alignItems: "center",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(6, minmax(0, 1fr))",
            },
          }}
        >
          {/* Name (multi) */}
          <Box sx={{ minWidth: 0 }}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={nameOpts}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={nameOpts.filter((o) => filterNames.includes(o.id))}
              onChange={(_, val, _r, details) => {
                const clicked = details?.option;
                if (clicked?.id === "__ALL__") {
                  setFilterNames(allNamesSelected ? [] : nameOptions);
                  return;
                }
                setFilterNames(val.filter((v) => v.id !== "__ALL__").map((v) => v.id));
              }}
              renderOption={(props, option) => {
                const isAll = option.id === "__ALL__";
                const checked = isAll ? allNamesSelected : filterNames.includes(option.id);
                const indeterminate =
                  isAll && filterNames.length > 0 && !allNamesSelected;
                return (
                  <li {...props}>
                    <Checkbox size="small" checked={checked} indeterminate={indeterminate} sx={{ mr: 1 }} />
                    {option.label}
                  </li>
                );
              }}
              renderTags={() => null}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={
                    filterNames.length ? `${filterNames.length} selected` : "Entities"
                  }
                />
              )}
            />
          </Box>

          {/* Type (multi) */}
          <Box sx={{ minWidth: 0 }}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={typeOpts}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={typeOpts.filter((o) => filterTypes.includes(o.id))}
              onChange={(_, val, _r, details) => {
                const clicked = details?.option;
                if (clicked?.id === "__ALL__") {
                  setFilterTypes(allTypesSelected ? [] : typeOptions);
                  return;
                }
                setFilterTypes(val.filter((v) => v.id !== "__ALL__").map((v) => v.id));
              }}
              renderOption={(props, option) => {
                const isAll = option.id === "__ALL__";
                const checked = isAll ? allTypesSelected : filterTypes.includes(option.id);
                const indeterminate =
                  isAll && filterTypes.length > 0 && !allTypesSelected;
                return (
                  <li {...props}>
                    <Checkbox size="small" checked={checked} indeterminate={indeterminate} sx={{ mr: 1 }} />
                    {option.label}
                  </li>
                );
              }}
              renderTags={() => null}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={
                    filterTypes.length ? `${filterTypes.length} selected` : "Type"
                  }
                />
              )}
            />
          </Box>

          {/* spacer to keep 6 columns filled if you want later filters */}
          <Box sx={{ minWidth: 0 }} />


        {/* optional Apply button */}
          <Box sx={{ minWidth: 0 }}>
            
          </Box>

          {/* Dates */}
          <Box sx={{ minWidth: 0 }}>
            <TextField
              label="Start Date"
              size="small"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: endDate }}
              fullWidth
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <TextField
              label="End Date"
              size="small"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: startDate }}
              fullWidth
            />
          </Box>

          
        </Box>
      </Paper>

      {/* Table */}
      <Paper sx={{ p: 2, width: "100%" }}>
        <Table
          size="small"
          sx={{
            width: "100%",
            "& th, & td": { py: 0.5 },
          }}
        >
          <TableHead sx={{ "& th": { py: 0.7 } }}>
            <TableRow>
              <TableCell sx={{ width: 80 }}>
                <TableSortLabel
                  active={sortField === "srno"}
                  direction={sortField === "srno" ? sortOrder : "asc"}
                  onClick={() => handleSort("srno")}
                >
                  Sr. No.
                </TableSortLabel>
              </TableCell>

              <TableCell>
                <TableSortLabel
                  active={sortField === "name"}
                  direction={sortField === "name" ? sortOrder : "asc"}
                  onClick={() => handleSort("name")}
                >
                  Entity Name
                </TableSortLabel>
              </TableCell>

              <TableCell>
                <TableSortLabel
                  active={sortField === "type"}
                  direction={sortField === "type" ? sortOrder : "asc"}
                  onClick={() => handleSort("type")}
                >
                  Type
                </TableSortLabel>
              </TableCell>

              <TableCell>
                <TableSortLabel
                  active={sortField === "createdOn"}
                  direction={sortField === "createdOn" ? sortOrder : "asc"}
                  onClick={() => handleSort("createdOn")}
                >
                  Created On
                </TableSortLabel>
              </TableCell>

              <TableCell>
                <TableSortLabel
                  active={sortField === "createdBy"}
                  direction={sortField === "createdBy" ? sortOrder : "asc"}
                  onClick={() => handleSort("createdBy")}
                >
                  Created By
                </TableSortLabel>
              </TableCell>

              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paged.map((e, i) => (
              <TableRow
                key={e.id}
                hover
                sx={{
                  "& td": { py: 0.5 },
                  "& .MuiIconButton-root": { p: 0.5 },
                }}
              >
                <TableCell>{page * rowsPerPage + i + 1}</TableCell>
                <TableCell>{e.name}</TableCell>
                <TableCell>{e.type}</TableCell>
                <TableCell>{formatDMY(e.createdOn)}</TableCell>
                <TableCell>{e.createdBy || "—"}</TableCell>

                <TableCell sx={{ minWidth: 90 }}>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleOpenDialog(e)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onRequestDeleteEntity(e)}
                    >
                      <DeleteOutlineOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={sorted.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRpp}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Add/Edit dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{currentEntity.id ? "Edit Entity" : "Add Entity"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            sx={{ mt: 2 }}
            value={currentEntity.name}
            onChange={(e) =>
              setCurrentEntity({ ...currentEntity, name: e.target.value })
            }
          />
          <TextField
            select
            label="Type"
            fullWidth
            sx={{ mt: 2 }}
            value={currentEntity.type}
            onChange={(e) =>
              setCurrentEntity({ ...currentEntity, type: e.target.value })
            }
          >
            {mergedTypes.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
             ))}

          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEntity}>
            {currentEntity.id ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm delete */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Delete entity?</DialogTitle>
        <DialogContent>
          <DialogContentText variant="body2" color="text.secondary">
            {pendingDelete ? (
              <>
                This will permanently remove{" "}
                <strong>{pendingDelete.name}</strong>.
              </>
            ) : (
              "This will permanently remove the entity."
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={onConfirmDeleteEntity}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Entities;
